import React, { memo, useEffect, useRef, useState } from "react"
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  useTheme,
  Box,
  Tooltip,
  makeStyles,
  Divider,
  Typography,
  Button,
} from "@material-ui/core"
import { CheckBox, CheckBoxOutlineBlank } from "@material-ui/icons"
import moment from "moment-timezone"
import useDragScroll from "use-drag-scroll"
import { useHistory, useParams } from "react-router-dom"
import { toId, useJobUtils } from "../../utils"
import { TASK_RESPONSE_TYPE, useLazyQueryAdminableUser } from "../../data"
import { FlexBox, FormatDateFormal, FormatHumanTime, Icon, ImagesViewer, PaperBox, TimestampUserPanel } from ".."

const useTooltipStyles = makeStyles((theme) => ({
  tooltipButton: {
    display: "inline-block",
    alignItems: "center",
    textDecoration: "none",
    backgroundColor: "transparent",
    border: "none",
    fontWeight: "normal",
    fontFamily: theme.typography.fontFamily,
    fontSize: "inherit",
    lineHeight: "inherit",
    cursor: "pointer",
    outline: "none",
    margin: 0,
    padding: 0,
  },
  valueButton: {
    color: theme.palette.text.light,
  },
  tooltipContent: {
    color: theme.palette.text.light,
    maxWidth: 550,
    fontSize: 14,
    fontWeight: "500",
    lineHeight: "20px",
    cursor: "pointer",
  },
  tooltipResponseValue: {
    color: theme.palette.text.light,
    fontSize: 14,
    lineHeight: "14px",
    fontWeight: "normal",
  },
  jobTitle: {
    color: theme.palette.text.main,
    fontSize: 16,
    fontWeight: "500",
    lineHeight: "20px",
  },
  tooltip: {
    backgroundColor: "Transparent",
    maxWidth: "none",
  },
  anchor: {
    left: "8px !important",
    top: "0px !important",
    transform: "none !important",
  },
}))

const useTableStyles = makeStyles((theme) => ({
  stepCell: {
    position: "sticky",
    left: 0,
    maxWidth: 400,
    backgroundColor: "white",
    overflowWrap: "break-word",
  },
  stepName: {
    maxWidth: 400,
    minWidth: 200,
    overflowWrap: "break-word",
  },
  valueCell: {},
  valueResponse: {
    whiteSpace: "nowrap",
    maxWidth: 120,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  templatedValueResponse: {
    maxWidth: 120,
    overflow: "hidden",
    textTransform: "uppercase",
  },
  photosResponse: {
    width: 120,
  },
  valueEmpty: {
    fontStyle: "italic",
    fontWeight: "normal",
    color: theme.palette.text.faint,
  },
  notes: {
    color: theme.palette.text.light,
    fontSize: 11,
    fontStyle: "italic",
  },
}))

const UserTooltip = ({ jobStep, children, anchor }) => {
  const classes = useTooltipStyles()
  const { days, locations, groups } = useParams()
  const history = useHistory()
  const { isStepValueResponseType } = useJobUtils()
  const [loadUser, { data }] = useLazyQueryAdminableUser()
  const [open, setOpen] = useState(false)
  const {
    step: { responseType, response, skip, completedAt, completedBy },
  } = jobStep

  useEffect(() => {
    if (open) {
      loadUser({ variables: { id: completedBy } })
    }
  }, [completedBy, loadUser, open])

  const handleOpen = () => {
    if (!open) setOpen(true)
  }

  const handleUserClick = () => {
    history.push(`/reports/people/${days || 7}/${locations || "all"}/${groups || "all"}/${toId(data.adminableUser)}`)
  }

  return (
    <Tooltip
      title={
        <PaperBox className={classes.tooltipContent} onClick={handleUserClick}>
          {isStepValueResponseType(responseType) && !skip && (
            <>
              <Box p={2} className={classes.tooltipResponseValue}>
                {response}
              </Box>
              <Divider />
            </>
          )}
          {data && (
            <TimestampUserPanel
              user={data.adminableUser}
              timestamp={completedAt}
              hoverText={
                <Box mt={1}>
                  <Button variant="outlined" size="small">
                    Reports&nbsp;
                    <Icon name="forward" fontSize="small" />
                  </Button>
                </Box>
              }
            />
          )}
        </PaperBox>
      }
      classes={{ tooltip: classes.tooltip, popper: anchor ? classes.anchor : null }}
      enterDelay={1000}
      enterTouchDelay={1000}
      leaveTouchDelay={2000}
      onOpen={handleOpen}
      interactive
      PopperProps={{
        popperOptions: {
          positionFixed: true,
        },
      }}
    >
      <button type="button" className={`${classes.tooltipButton} ${classes.valueButton}`}>
        {children}
      </button>
    </Tooltip>
  )
}

const JobStepDataTableRow = memo(function JobStepDataTableRow({
  step: { name, responseType: processStepResponseType },
  jobs,
}) {
  const classes = useTableStyles()
  const theme = useTheme()
  const [viewing, setViewing] = useState(false)
  const { getStepStatus, isStepValueResponseType } = useJobUtils()

  const handleOpen = () => {
    setViewing(true)
  }

  const handleClose = () => {
    setViewing(false)
  }

  return (
    <TableRow>
      <TableCell className={classes.stepCell}>
        <Box className={classes.stepName} title={name}>
          {name}
        </Box>
      </TableCell>
      {jobs.map((jobStep) => {
        const {
          id,
          step: { responseType: stepResponseType, response, responseUploads, notes },
        } = jobStep
        const responseType = stepResponseType || processStepResponseType
        const status = getStepStatus(jobStep)
        const userStatus = ["complete", "overdue", "skipped"].includes(status)
        const valueResponseType = isStepValueResponseType(responseType)
        const overdue = status === "overdue"

        return (
          <TableCell key={id} align="center" className={classes.valueCell} style={{ cursor: "grab", width: 150 }}>
            {userStatus && (
              <UserTooltip jobStep={jobStep} status={status} anchor={viewing}>
                <>
                  {["complete", "overdue"].includes(status) && responseType === TASK_RESPONSE_TYPE.NONE && (
                    <Box>
                      <CheckBox style={{ fill: overdue ? theme.palette.warning.light : theme.palette.success.main }} />
                    </Box>
                  )}
                  {status === "skipped" && (
                    <Box>
                      <Icon name="skip" style={{ fill: theme.palette.skipped.checkbox }} />
                    </Box>
                  )}
                  {status === "complete" && responseType === TASK_RESPONSE_TYPE.PHOTOS && (
                    <FlexBox className={classes.photosResponse} justifyContent="center">
                      <ImagesViewer
                        uploads={responseUploads}
                        onOpen={handleOpen}
                        onClose={handleClose}
                        max={3}
                        width={50}
                        height={50}
                      />
                    </FlexBox>
                  )}
                  {status === "complete" && valueResponseType && (
                    <Box
                      className={
                        responseType === TASK_RESPONSE_TYPE.YES_NO_NA
                          ? classes.templatedValueResponse
                          : classes.valueResponse
                      }
                    >
                      {response}
                      {responseType === TASK_RESPONSE_TYPE.NUMBER_RATING_5 && <>/5</>}
                      {responseType === TASK_RESPONSE_TYPE.NUMBER_RATING_10 && <>/10</>}
                    </Box>
                  )}
                </>
              </UserTooltip>
            )}
            {!userStatus && (
              <Box title="Uncompleted">
                {!valueResponseType && responseType !== TASK_RESPONSE_TYPE.PHOTOS && (
                  <CheckBoxOutlineBlank style={{ fill: theme.palette.incomplete.main }} />
                )}
                {(valueResponseType || responseType === TASK_RESPONSE_TYPE.PHOTOS) && (
                  <Box className={classes.valueEmpty}>&lt;empty&gt;</Box>
                )}
              </Box>
            )}
            {notes && (
              <Box className={classes.notes}>
                {notes.map((note) => (
                  <Tooltip title={`${note.author?.fullName}`} placement="right">
                    <Box key={toId(note)}>&ldquo;{note.text}&rdquo;</Box>
                  </Tooltip>
                ))}
              </Box>
            )}
          </TableCell>
        )
      })}
    </TableRow>
  )
})

const JobTooltip = memo(function JobTooltip({ job, location, children }) {
  const classes = useTooltipStyles()
  const [open, setOpen] = useState(false)
  const { title, availableFrom, dueAt } = job

  const handleOpen = () => {
    if (!open) setOpen(true)
  }

  return (
    <Tooltip
      title={
        <PaperBox className={classes.tooltipContent}>
          <Box p={2}>
            <Typography component="h1" className={classes.jobTitle}>
              {title}
            </Typography>
            <Box>
              <FormatDateFormal value={moment(availableFrom).tz(location.timeZone)} />
            </Box>
            {dueAt && (
              <Box mt={1}>
                Due at <FormatHumanTime value={moment(dueAt).tz(location.timeZone)} />
              </Box>
            )}
          </Box>
        </PaperBox>
      }
      classes={{ tooltip: classes.tooltip }}
      enterTouchDelay={100}
      leaveTouchDelay={2000}
      onOpen={handleOpen}
    >
      <button type="button" className={classes.tooltipButton}>
        {children}
      </button>
    </Tooltip>
  )
})

const JobStepDataTable = memo(function JobStepDataTable({ data, location }) {
  const tableRef = useRef()

  useDragScroll({
    sliderRef: tableRef,
    momentumVelocity: 0,
  })

  if (!data?.process) {
    return
  }

  const {
    process: { steps },
    jobs,
  } = data

  const getJobSteps = (step) =>
    jobs.map(({ id, dueAt, steps: jobSteps }) => ({
      id,
      dueAt,
      step: jobSteps.find((jobStep) => jobStep.step === step.id || jobStep.name === step.name) || {},
    }))

  return (
    <div ref={tableRef} style={{ overflow: "auto" }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell style={{ position: "sticky", left: 0, backgroundColor: "white" }}>&nbsp;</TableCell>
            {jobs.map((job) => {
              const { id, availableFrom } = job
              return (
                <TableCell key={id} align="center" style={{ whiteSpace: "nowrap", width: 150 }}>
                  <JobTooltip job={job} location={location}>
                    {moment(availableFrom).tz(location.timeZone).format("D MMM YY")}
                  </JobTooltip>
                </TableCell>
              )
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {steps.map((step) => (
            <JobStepDataTableRow key={toId(step)} step={step} jobs={getJobSteps(step)} />
          ))}
        </TableBody>
      </Table>
    </div>
  )
})

export { JobStepDataTable }
